/*
 * Hook de gestion du formulaire de création / édition d'une application.
 * Il centralise :
 * - le chargement des données
 * - l'initialisation du formulaire react-hook-form
 * - la synchronisation des données asynchrones en mode édition
 * - les actions de soumission
 */

import { skipToken } from '@reduxjs/toolkit/query'
import {
  useCreateApplicationMutation,
  useGetApplicationQuery,
  useUpdateApplicationMutation,
} from 'core/api/application'
import { toast } from 'core/components/common/Toast'
import {
  ERROR_DUPLICATE_NOM_APPLICATION,
  ERROR_DUPLICATE_NOM_TECHNIQUE,
  ERROR_INVALID_NOM_TECHNIQUE,
} from 'core/constants/error'
import { ROUTE_PATHS } from 'core/constants/routePath'
import { UserContext } from 'core/contexts/UserContext'
import { ResponsablesMode } from 'core/interfaces/Application'
import { IIconeService, IService } from 'core/interfaces/Service'
import { getIconeInfo } from 'core/utils/service'
import { useGetAllServiceQuery } from 'lauria/api/service'
import {
  getAdditionalMcpUtilisateursFromApplication,
  getApplicationMcpUtilisateurIds,
  mapResponsableToSelectedUtilisateur,
} from 'lauria/utils/applicationFormHydration'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { FieldErrors, Resolver, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router'
import { useParams } from 'react-router-dom'

export type ApplicationForm = {
  intitule: string
  nomTechnique: string
  description: string
  accroche: string
  idService: string
  tags: string
  responsablesMode?: ResponsablesMode
  responsablesUtilisateurIds: number[] | null
  accessibleMcp: 'true' | 'false'
  mcpUtilisateurIds: number[] | null
}

const DEFAULT_FORM_VALUES: ApplicationForm = {
  intitule: '',
  nomTechnique: '',
  description: '',
  accroche: '',
  idService: '',
  tags: '',
  responsablesMode: undefined,
  responsablesUtilisateurIds: null,
  accessibleMcp: 'false',
  mcpUtilisateurIds: null,
}

function buildMcpUtilisateurIds(
  responsablesUtilisateurIds: number[] | null,
  additionalMcpUtilisateurIds: number[]
): number[] | null {
  const combinedIds = [
    ...(responsablesUtilisateurIds ?? []),
    ...additionalMcpUtilisateurIds,
  ]
  const uniqueIds = Array.from(new Set(combinedIds))

  return uniqueIds.length > 0 ? uniqueIds : null
}

function haveSameIds(left: number[], right: number[]): boolean {
  if (left.length !== right.length) {
    return false
  }

  const sortedLeft = [...left].sort((a, b) => a - b)
  const sortedRight = [...right].sort((a, b) => a - b)

  return sortedLeft.every((id, index) => id === sortedRight[index])
}

export const useEditApplication = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user: currentUser } = useContext(UserContext)
  const { idApplication } = useParams()

  const applicationId = idApplication ? parseInt(idApplication, 10) : undefined
  const creation = applicationId === undefined

  const [openIconSelector, setOpenIconSelector] = useState<boolean>(false)
  const [selectedService, setSelectedService] = useState<IService | null>(null)
  const [selectedTags, setSelectedTags] = useState<
    { id: number; label: string }[]
  >([])
  const [icone, setIcone] = useState<IIconeService | null>(null)
  const [selectedResponsables, setSelectedResponsables] = useState<
    { id: number; label: string }[]
  >([])
  const [selectedMcpUtilisateurs, setSelectedMcpUtilisateurs] = useState<
    { id: number; label: string }[]
  >([])

  const { data: services, isLoading: isLoadingServices } =
    useGetAllServiceQuery()

  const [createApplication, { isLoading: isLoadingCreateApplication }] =
    useCreateApplicationMutation()

  const [updateApplication, { isLoading: isLoadingUpdateApplication }] =
    useUpdateApplicationMutation()

  const { data: application, isLoading: isLoadingApp } = useGetApplicationQuery(
    applicationId ? { applicationId } : skipToken
  )

  const resolver: Resolver<ApplicationForm> = async (data) => {
    const errors: FieldErrors<ApplicationForm> = {}

    if (!data.intitule) {
      errors.intitule = { type: 'required', message: '' }
    }
    if (!data.description) {
      errors.description = { type: 'required', message: '' }
    }
    if (!data.accroche) {
      errors.accroche = { type: 'required', message: '' }
    }
    if (!data.nomTechnique) {
      errors.nomTechnique = { type: 'required', message: '' }
    }
    if (!data.idService) {
      errors.idService = { type: 'required', message: '' }
    }
    if (!data.tags) {
      errors.tags = { type: 'required', message: '' }
    }

    // Création seulement : responsablesMode obligatoire
    if (creation && !data.responsablesMode) {
      errors.responsablesMode = { type: 'required', message: '' }
    }

    // Dès qu'on choisit selected_users, la liste devient obligatoire
    if (
      data.responsablesMode === 'selected_users' &&
      selectedResponsables.length === 0
    ) {
      errors.responsablesUtilisateurIds = { type: 'required', message: '' }
    }

    return {
      values: Object.keys(errors).length > 0 ? {} : data,
      errors,
    }
  }

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isValid, dirtyFields, isSubmitted },
    setValue,
    getValues,
    reset,
    watch,
  } = useForm<ApplicationForm>({
    defaultValues: DEFAULT_FORM_VALUES,
    resolver,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })

  const responsablesMode = watch('responsablesMode')
  const accessibleMcp = watch('accessibleMcp')
  const responsablesUtilisateurIds = watch('responsablesUtilisateurIds')

  const state = useMemo(
    () => ({
      selectedService,
      setSelectedService,
      selectedTags,
      setSelectedTags,
      icone,
      setIcone,
      selectedResponsables,
      setSelectedResponsables,
      selectedMcpUtilisateurs,
      setSelectedMcpUtilisateurs,
    }),
    [
      selectedService,
      selectedTags,
      icone,
      selectedResponsables,
      selectedMcpUtilisateurs,
    ]
  )

  const shouldSkipNextTagsReset = useRef(false)
  const shouldSkipResponsablesDirty = useRef(false)
  const shouldSkipMcpDirty = useRef(false)
  const initialResponsableIdsRef = useRef<number[]>([])
  const previousResponsablesModeRef = useRef<ResponsablesMode | undefined>(
    undefined
  )
  const previousTagIdsRef = useRef<number[]>([])

  useEffect(() => {
    if (responsablesMode === undefined) {
      return
    }

    const previousMode = previousResponsablesModeRef.current
    previousResponsablesModeRef.current = responsablesMode

    if (
      previousMode === 'selected_users' &&
      responsablesMode !== 'selected_users'
    ) {
      setSelectedResponsables([])
    }
  }, [responsablesMode])

  useEffect(() => {
    if (accessibleMcp === 'true') {
      return
    }

    setSelectedMcpUtilisateurs([])
    setValue('mcpUtilisateurIds', null, { shouldValidate: isSubmitted })
  }, [accessibleMcp, setValue, isSubmitted])

  useEffect(() => {
    if (!application) {
      return
    }

    const hydratedTags =
      application.tags?.map((tag) => ({
        id: tag.id,
        label: tag.nomTag,
      })) ?? []

    const hydratedResponsables =
      application.responsables?.map(mapResponsableToSelectedUtilisateur) ?? []
    const responsableIds = hydratedResponsables.map(
      (responsable) => responsable.id
    )
    const applicationMcpUtilisateurIds =
      getApplicationMcpUtilisateurIds(application)
    const hydratedMcpUtilisateurs = application.accessibleMcp
      ? getAdditionalMcpUtilisateursFromApplication(application, responsableIds)
      : []

    // Important :
    // react-hook-form ne met pas à jour les defaultValues automatiquement
    // quand les données arrivent après le premier render.
    reset({
      intitule: application.nomApplication ?? '',
      nomTechnique: application.nomTechnique ?? '',
      description: application.description ?? '',
      accroche: application.accroche ?? '',
      idService: application.service?.id?.toString() ?? '',
      tags: hydratedTags.length > 0 ? JSON.stringify(hydratedTags) : '',
      responsablesMode: application.responsablesMode ?? 'all_services_users',
      responsablesUtilisateurIds:
        application.responsablesMode === 'selected_users' &&
        responsableIds.length > 0
          ? responsableIds
          : null,
      accessibleMcp: application.accessibleMcp ? 'true' : 'false',
      mcpUtilisateurIds:
        application.accessibleMcp && applicationMcpUtilisateurIds.length > 0
          ? applicationMcpUtilisateurIds
          : null,
    })

    setSelectedService(application.service ?? null)

    // On ignore le prochain effet déclenché par le changement de tags
    shouldSkipNextTagsReset.current = true
    shouldSkipResponsablesDirty.current = true
    shouldSkipMcpDirty.current = true
    setSelectedTags(hydratedTags)

    initialResponsableIdsRef.current = responsableIds
    previousResponsablesModeRef.current =
      application.responsablesMode ?? 'all_services_users'
    previousTagIdsRef.current = hydratedTags.map((tag) => tag.id)
    setSelectedResponsables(hydratedResponsables)
    setSelectedMcpUtilisateurs(hydratedMcpUtilisateurs)
    setIcone(application.icone ? getIconeInfo(application.icone) : null)
  }, [application, reset])

  useEffect(() => {
    setValue(
      'tags',
      selectedTags.length > 0 ? JSON.stringify(selectedTags) : '',
      { shouldValidate: isSubmitted }
    )
  }, [selectedTags, setValue, isSubmitted])

  useEffect(() => {
    const nextResponsablesUtilisateurIds =
      selectedResponsables.length > 0
        ? selectedResponsables.map((responsable) => responsable.id)
        : null

    setValue('responsablesUtilisateurIds', nextResponsablesUtilisateurIds, {
      shouldValidate: isSubmitted,
      shouldDirty: !shouldSkipResponsablesDirty.current,
    })
    shouldSkipResponsablesDirty.current = false
  }, [selectedResponsables, setValue, isSubmitted])

  useEffect(() => {
    if (accessibleMcp !== 'true') {
      return
    }

    const responsableIdsForMcp =
      responsablesMode === 'selected_users'
        ? selectedResponsables.length > 0
          ? selectedResponsables.map((responsable) => responsable.id)
          : null
        : null

    setValue(
      'mcpUtilisateurIds',
      buildMcpUtilisateurIds(
        responsableIdsForMcp,
        selectedMcpUtilisateurs.map((mcpUtilisateur) => mcpUtilisateur.id)
      ),
      {
        shouldValidate: isSubmitted,
        shouldDirty: !shouldSkipMcpDirty.current,
      }
    )
    shouldSkipMcpDirty.current = false
  }, [
    accessibleMcp,
    responsablesMode,
    selectedResponsables,
    selectedMcpUtilisateurs,
    setValue,
    isSubmitted,
  ])

  useEffect(() => {
    const currentTagIds = selectedTags.map((tag) => tag.id)

    if (shouldSkipNextTagsReset.current) {
      shouldSkipNextTagsReset.current = false
      return
    }

    if (haveSameIds(currentTagIds, previousTagIdsRef.current)) {
      return
    }

    previousTagIdsRef.current = currentTagIds
    setSelectedResponsables([])
  }, [selectedTags])

  const handleIntituleBlur = () => {
    if (application) {
      return
    }

    const intituleValue = getValues('intitule')
    if (!intituleValue) {
      return
    }

    if (!dirtyFields.accroche) {
      setValue(
        'accroche',
        t('applications.form.accrocheDefault', { intitule: intituleValue }),
        { shouldDirty: true, shouldValidate: true }
      )
    }

    if (!dirtyFields.nomTechnique) {
      const formattedValue = intituleValue
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '')

      setValue('nomTechnique', formattedValue, {
        shouldDirty: true,
        shouldValidate: true,
      })
    }
  }

  const handleSelectService = (service: IService) => {
    const nextService = selectedService?.id === service.id ? null : service

    setSelectedService(nextService)
    setValue('idService', nextService ? nextService.id.toString() : '', {
      shouldValidate: isSubmitted,
    })
  }

  const handleTagRemove = (id: number) => {
    setSelectedTags((prev) => prev.filter((tag) => tag.id !== id))
  }

  /**
   * Supprime un responsable de la liste des responsables sélectionnés
   */
  const handleResponsableRemove = (idResponsable: number) => {
    setSelectedResponsables((prev) =>
      prev.filter((responsable) => responsable.id !== idResponsable)
    )
  }

  const handleMcpUtilisateurRemove = (idMcpUtilisateur: number) => {
    setSelectedMcpUtilisateurs((prev) =>
      prev.filter((mcpUtilisateur) => mcpUtilisateur.id !== idMcpUtilisateur)
    )
  }

  const cancelAction = () => {
    if (creation) {
      navigate(generatePath(ROUTE_PATHS.DASHBOARD))
      return
    }

    navigate(
      generatePath(ROUTE_PATHS.PROMPTS, {
        idApplication: String(applicationId),
      })
    )
  }

  const catchErrors = (error: unknown) => {
    const typedError = error as {
      status?: number | string
      data?: { detail?: string }
    }

    if (typedError.status === 409) {
      if (typedError.data?.detail === ERROR_DUPLICATE_NOM_APPLICATION) {
        setError('intitule', {
          type: 'custom',
          message: t('applications.form.errors.duplicateIntitule'),
        })
        return
      }

      if (typedError.data?.detail === ERROR_DUPLICATE_NOM_TECHNIQUE) {
        setError('nomTechnique', {
          type: 'custom',
          message: t('applications.form.errors.duplicateNomTechnique'),
        })
        return
      }
    }

    if (
      typedError.status === 422 &&
      typedError.data?.detail === ERROR_INVALID_NOM_TECHNIQUE
    ) {
      setError('nomTechnique', {
        type: 'custom',
        message: t('applications.form.errors.invalidNomTechnique'),
      })
      return
    }

    // 403/404/500/FETCH_ERROR sont déjà pris en charge globalement par apiErrorMiddleware
    if (
      typedError.status === 403 ||
      typedError.status === 404 ||
      typedError.status === 500 ||
      typedError.status === 'FETCH_ERROR'
    ) {
      return
    }

    toast.error(t('applications.form.errors.saveError'))
  }

  /**
   * Construit le payload des responsables en fonction du mode choisi
   */
  const buildResponsablesPayload = (
    data: ApplicationForm,
    selectedResponsableIds: number[]
  ): {
    responsablesMode?: ResponsablesMode
    responsablesUtilisateurIds?: number[]
  } => {
    if (creation) {
      if (data.responsablesMode === 'selected_users') {
        return {
          responsablesMode: data.responsablesMode,
          responsablesUtilisateurIds: selectedResponsableIds,
        }
      }

      return data.responsablesMode
        ? { responsablesMode: data.responsablesMode }
        : {}
    }

    const responsablesChanged = !haveSameIds(
      selectedResponsableIds,
      initialResponsableIdsRef.current
    )

    // En update, on n'envoie que si le champ a été modifié
    if (
      !data.responsablesMode ||
      (!dirtyFields.responsablesMode &&
        !dirtyFields.responsablesUtilisateurIds &&
        !responsablesChanged)
    ) {
      return {}
    }

    if (data.responsablesMode === 'selected_users') {
      return {
        responsablesMode: data.responsablesMode,
        responsablesUtilisateurIds: selectedResponsableIds,
      }
    }

    return {
      responsablesMode: data.responsablesMode,
    }
  }

  const onSubmit = handleSubmit((data) => {
    const selectedResponsableIds = selectedResponsables.map(
      (responsable) => responsable.id
    )
    const responsablesPayload = buildResponsablesPayload(
      data,
      selectedResponsableIds
    )
    const accessibleMcp = data.accessibleMcp === 'true'
    const mcpUtilisateurIds = accessibleMcp
      ? (data.mcpUtilisateurIds ?? undefined)
      : undefined

    if (application) {
      updateApplication({
        idApplication: application.id,
        idService: parseInt(data.idService, 10),
        nomApplication: data.intitule,
        tags: selectedTags.map((tag) => tag.id),
        description: data.description,
        accroche: data.accroche,
        icone,
        accessibleMcp,
        mcpUtilisateurIds,
        ...responsablesPayload,
      })
        .unwrap()
        .then((updatedApplication) => {
          if (updatedApplication.id) {
            navigate(
              generatePath(ROUTE_PATHS.PROMPTS, {
                idApplication: updatedApplication.id,
              })
            )
            toast.success(t('applications.form.edit.success'))
          }
        })
        .catch(catchErrors)

      return
    }

    const idClient = currentUser?.idClient
    if (!idClient) {
      return
    }

    createApplication({
      idClient,
      idService: parseInt(data.idService, 10),
      nomApplication: data.intitule,
      nomTechnique: data.nomTechnique,
      tags: selectedTags.map((tag) => tag.id),
      description: data.description,
      accroche: data.accroche,
      icone,
      accessibleMcp,
      mcpUtilisateurIds,
      responsablesMode:
        responsablesPayload.responsablesMode as ResponsablesMode,
      responsablesUtilisateurIds:
        responsablesPayload.responsablesUtilisateurIds,
    })
      .unwrap()
      .then((createdApplication) => {
        if (createdApplication.id) {
          navigate(
            generatePath(ROUTE_PATHS.PROMPTS, {
              idApplication: createdApplication.id,
            })
          )
          toast.success(t('applications.form.success'))
        }
      })
      .catch(catchErrors)
  })

  const handleSelectIcon = (icon: IIconeService) => {
    setOpenIconSelector(false)
    setIcone(icon)
  }

  return {
    services,
    isLoading:
      isLoadingServices ||
      (isLoadingApp && !application) ||
      isLoadingCreateApplication ||
      isLoadingUpdateApplication,
    state,
    handleSelectService,
    register,
    setValue,
    watch,
    errors,
    handleSubmit,
    handleTagRemove,
    creation,
    isValid,
    cancelAction,
    onSubmit,
    handleIntituleBlur,
    openIconSelector,
    setOpenIconSelector,
    handleSelectIcon,
    handleResponsableRemove,
    handleMcpUtilisateurRemove,
    responsablesMode,
    accessibleMcp,
    responsablesUtilisateurIds,
  }
}
